import { makeAutoObservable } from 'mobx';
import moment from 'moment';
import i18n from '../../../i18n';
import { DataAnalysisCartogram } from '../../../models';

/** history/cartogram 折线图 Y 轴指标，与接口按日项字段名一致 */
export type HistoryCartogramMetric =
    | 'actual_amount_total'
    | 'take_out_total'
    | 'eat_in_total';
import { HistoryPeriod } from '../../../models/abstract/history-period.entity';
import apiManage from '../../../request';
import { FetchCancelGate } from '../../../utils/fetch-cancel-gate';
import { CancelToken } from 'axios';

class HistoryDataStore {
    // loading
    loading: boolean = true;
    // 即时营业数据
    data: HistoryPeriod = {};
    // 图标数据
    chartData: {
        lastMonthData: number[];
        thisMonthData: number[];
    } = {
            lastMonthData: [],
            thisMonthData: []
        };
    // 图标数据
    chartConfig: {
        XAxisText: string[]
    } = {
            XAxisText: []
        };

    /** 折线图当前选中的 Y 轴指标（cartogram 按日字段名） */
    selectListValue: HistoryCartogramMetric = 'actual_amount_total';
    // 营业数据图表数据
    dataAnalysisCartogram: DataAnalysisCartogram = {};
    // 开始时间
    startDate: string = '';
    // 结束时间
    endDate: string = '';
    // 是否成功获取了数据
    errorMessage: string = '';

    /** 列表+图表请求取消门闩（日期快速切换时 cancel 上一请求） */
    private listFetchGate = new FetchCancelGate();

    constructor() {
        makeAutoObservable(this)
    }

    /**
     * 获取数据- 获取餐厅详情
     */
    async fetchData(stringId: string, startDate: string, endDate: string) {
        const { cancelToken, seq } = this.listFetchGate.begin();
        this.setLoading(true);
        this.startDate = startDate;
        this.endDate = endDate;
        this.setErrorMessage('');
        try {
            const res = await apiManage.get_data_analysis_history_period({
                string_id: stringId,
                start: startDate,
                end: endDate
            }, { cancelToken });
            if (!this.listFetchGate.isLatest(seq)) {
                return;
            }
            if (!res?.error) {
                const total_service_fee =(Number(res.total_service_fee || 0)) + (Number(res.total_delivery_service_fee || 0))
                this.setData({
                    ...res,
                    total_service_fee,
                })
                this.setErrorMessage('');
            } else {
                if (res.message !== 'canceled') {
                    if (res?.result?.statusCode === 404) {
                        this.setErrorMessage(i18n.t('business_analysis_page_text_restaurant_data_not_synchronized'));
                    } else {
                        this.setErrorMessage(i18n.t('business_analysis_page_msg_fetch_operate_info_failed'));
                    }
                }
            }
            await this.getSalesAnalysis(stringId, cancelToken, seq);
        } finally {
            this.listFetchGate.endLoading(seq, (value) => this.setLoading(value));
        }
    }

    /**
     * 获取每日营业数据
     * @param seq - 与 fetchData 同一代请求序号，过期响应不写 chart
     */
    async getSalesAnalysis(stringId: string, cancelToken: CancelToken, seq?: number) {
        const params = {
            string_id: stringId,
            start: this.startDate,
            end: this.endDate,
        }
        const res = await apiManage.get_data_analysis_history_cartogram(params, { cancelToken });
        if (seq !== undefined && !this.listFetchGate.isLatest(seq)) {
            return;
        }
        if (!res?.error) {
            this.dataAnalysisCartogram = res;
            this.updateCharts();
        }
    }

    setSeizeASeat(diff: number): number[] {
        return new Array(diff).fill(0);
    }


    updateCharts() {
        const { dataAnalysisCartogram, startDate, endDate } = this;
        const { returnCartogramOldResult, returnCartogramResult } = dataAnalysisCartogram;
        // 算出选中的天数，设置X轴占位数量值
        const dayDiff = moment(endDate).diff(moment(startDate), 'day') + 1;
        // x轴天数文本
        const XAxisText = this.setSeizeASeat(dayDiff).map(
            (item, index) => moment(Number(moment(startDate).format('x')) + (index * 24 * 60 * 60 * 1000)).format('YYYY-MM-DD')
        )

        // $ 占位 每天的数据
        const nowData = this.setSeizeASeat(dayDiff);
        const lastData = this.setSeizeASeat(dayDiff);
        // 转年月日
        const _startData = moment(startDate).format('YYYY-MM-DD');

        // 这个月
        returnCartogramResult?.forEach((item) => {
            const dayIndex = moment(item.days).diff(moment(_startData), 'day');
            nowData[dayIndex] = Number(item[this.selectListValue] || 0);
        })
        // 上个月
        returnCartogramOldResult?.forEach((item) => {
            const dayIndex = moment(item.days).diff(moment(_startData).subtract(1, 'M'), 'day');
            lastData[dayIndex] = Number(item[this.selectListValue] || 0);
        })

        this.setChartData({
            lastMonthData: lastData,
            thisMonthData: nowData
        });
        this.setChartConfig({
            XAxisText
        });
    }

    /**
     * 更新数据
     * @param value
     */
    setData(value: HistoryPeriod) {
        this.data = value
    }

    /**
     * 更新loading
     * @param value
     */
    setLoading(value: boolean) {
        this.loading = value
    }

    setChartData(val: {
        lastMonthData: number[];
        thisMonthData: number[];
    }) {
        this.chartData = val;

    }

    setChartConfig(val: {
        XAxisText: string[];
    }) {
        this.chartConfig = val;
    }

    updateSelectListValue(val: HistoryCartogramMetric) {
        this.selectListValue = val;
        this.updateCharts();
    }
    /**
     * 更新errorMessage
     */
    setErrorMessage(value: string) {
        this.errorMessage = value
    }
}

export default HistoryDataStore
